import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { toggleDsPick } from 'redux/mode/actions';
import { DesignSystemToken } from 'designSystem/types';
import * as S from './styles';

interface SpacingScaleProps {
	tokens: DesignSystemToken[];
}

const getSpacingPx = (token: DesignSystemToken): number => {
	if (token.value.kind !== 'length') return 0;
	return token.value.px ?? token.value.numericValue * 16;
};

const SpacingScale = ({ tokens }: SpacingScaleProps) => {
	const dispatch = useDispatch();
	const selectedPicks = useSelector((r: RootReducer) => r.mode.selectedDsPicks);

	const sorted = useMemo(
		() => [...tokens].sort((a, b) => getSpacingPx(a) - getSpacingPx(b)),
		[tokens]
	);

	const maxPx = useMemo(
		() => sorted.reduce((max, t) => Math.max(max, getSpacingPx(t)), 1),
		[sorted]
	);

	const isSelected = (token: DesignSystemToken) =>
		selectedPicks.some((p) => p.kind === 'token' && p.cssVariable === token.cssVariable);

	return (
		<S.Base>
			{sorted.map((token) => {
				const px = getSpacingPx(token);
				return (
					<S.Row
						key={token.id}
						type="button"
						$selected={isSelected(token)}
						onClick={() =>
							dispatch(toggleDsPick({ kind: 'token', cssVariable: token.cssVariable }))
						}
					>
						<S.Tooltip>{token.cssVariable}</S.Tooltip>
						<S.Name>{token.name}</S.Name>
						<S.BarTrack>
							<S.Bar $fraction={px / maxPx} />
						</S.BarTrack>
						<S.Value>{px}px</S.Value>
					</S.Row>
				);
			})}
		</S.Base>
	);
};

export default SpacingScale;
